import React, { useState, useEffect, useCallback } from 'react';
import * as Icons from 'lucide-react';

const API_BASE = window.location.hostname === 'localhost' ? 'http://localhost:8092' : `${window.location.protocol}//${window.location.hostname}:8092`;

const UserChatSettings = () => {
  const [activeTab, setActiveTab] = useState('live-chat');
  const [liveChats, setLiveChats] = useState([]);
  const [chatHistory, setChatHistory] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      const [liveRes, histRes, deptRes, agentRes] = await Promise.all([
        fetch(`${API_BASE}/api/wa/chat/live`, { headers }).catch(() => null),
        fetch(`${API_BASE}/api/wa/chat/history?limit=50`, { headers }).catch(() => null),
        fetch(`${API_BASE}/api/wa/chat/departments`, { headers }).catch(() => null),
        fetch(`${API_BASE}/api/wa/chat/agents`, { headers }).catch(() => null),
      ]);
      if (liveRes?.ok) { const d = await liveRes.json(); setLiveChats(d.chats || []); }
      if (histRes?.ok) { const d = await histRes.json(); setChatHistory(d.history || []); }
      if (deptRes?.ok) { const d = await deptRes.json(); setDepartments(d.departments || []); }
      if (agentRes?.ok) { const d = await agentRes.json(); setAgents(d.agents || []); }
    } catch (err) {
      console.error('Failed to fetch chat data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const statusColors = {
    active: { bg: '#dcfce7', text: '#166534', dot: '#22c55e' },
    waiting: { bg: '#fef3c7', text: '#92400e', dot: '#f59e0b' },
    resolved: { bg: '#f3f4f6', text: '#374151', dot: '#9ca3af' },
    online: { bg: '#dcfce7', text: '#166534', dot: '#22c55e' },
    away: { bg: '#fef3c7', text: '#92400e', dot: '#f59e0b' },
    offline: { bg: '#f3f4f6', text: '#374151', dot: '#9ca3af' },
    busy: { bg: '#fee2e2', text: '#991b1b', dot: '#ef4444' },
    open: { bg: '#dbeafe', text: '#1e40af', dot: '#3b82f6' },
    'auto-resolved': { bg: '#dbeafe', text: '#1e40af', dot: '#3b82f6' },
    auto_closed: { bg: '#f3e8ff', text: '#6b21a8', dot: '#a855f7' },
  };

  const getIcon = (name, size = 18) => {
    const Icon = Icons[name];
    return Icon ? <Icon style={{ width: size, height: size }} /> : null;
  };

  const tabs = [
    { key: 'live-chat', label: 'Live Chat', icon: 'MessageCircle' },
    { key: 'history', label: 'Chat History', icon: 'History' },
    { key: 'departments', label: 'Departments', icon: 'Building2' },
    { key: 'agents', label: 'Agents', icon: 'Users' },
  ];

  const renderEmpty = (icon, title, desc) => (
    <div className="text-center py-12">
      <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(37, 211, 102, 0.08)' }}>
        {getIcon(icon, 32)}
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-1">{title}</h3>
      <p className="text-sm text-gray-500">{desc}</p>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}>
              <Icons.Headphones className="w-5 h-5 text-white" />
            </div>
            Chat Management
          </h1>
          <p className="text-sm text-gray-500 mt-1">Manage live chats, agents, and departments</p>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Active Chats', value: liveChats.filter(c => c.status === 'active' || c.status === 'open').length || '0', icon: 'MessageCircle', color: '#25D366' },
          { label: 'Waiting', value: liveChats.filter(c => c.status === 'waiting').length || '0', icon: 'Clock', color: '#F59E0B' },
          { label: 'Online Agents', value: agents.filter(a => a.status === 'online').length || '0', icon: 'Users', color: '#128C7E' },
          { label: 'Departments', value: departments.length || '0', icon: 'Building2', color: '#34B7F1' },
        ].map((stat) => (
          <div key={stat.label} className="bg-white rounded-xl p-4 border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${stat.color}14`, color: stat.color }}>
                {getIcon(stat.icon, 18)}
              </div>
              <div>
                <p className="text-xl font-bold text-gray-900">{stat.value}</p>
                <p className="text-[11px] text-gray-500">{stat.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="flex border-b border-gray-100 overflow-x-auto">
          {tabs.map((tab) => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className="flex items-center gap-2 px-5 py-3.5 text-sm font-medium transition-all border-b-2 whitespace-nowrap"
              style={{
                borderColor: activeTab === tab.key ? '#25D366' : 'transparent',
                color: activeTab === tab.key ? '#25D366' : '#6b7280',
                background: activeTab === tab.key ? 'rgba(37, 211, 102, 0.04)' : 'transparent',
              }}
            >
              {getIcon(tab.icon, 16)}
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-6">
          {loading ? (
            <div className="text-center py-12">
              <Icons.Loader2 className="w-8 h-8 animate-spin mx-auto text-green-500 mb-3" />
              <p className="text-sm text-gray-500">Loading chat data...</p>
            </div>
          ) : (
            <>
              {/* Live Chat */}
              {activeTab === 'live-chat' && (
                liveChats.length === 0 ?
                  renderEmpty('MessageCircle', 'No Active Chats', 'Live chats will appear here when customers message your WhatsApp Business number.') :
                  <div className="space-y-3">
                    {liveChats.map((chat) => (
                      <div key={chat.id} className="flex items-center justify-between p-4 rounded-xl border border-gray-100 hover:border-green-200 hover:shadow-sm transition-all cursor-pointer">
                        <div className="flex items-center gap-4">
                          <div className="relative">
                            <div className="w-11 h-11 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}>
                              {(chat.contact_name || 'C').charAt(0)}
                            </div>
                            <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white" style={{ background: statusColors[chat.status]?.dot || '#9ca3af' }} />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900 text-sm">{chat.contact_name || `Contact #${chat.contact_id}`}</h3>
                            <p className="text-xs text-gray-500 mt-0.5">{chat.last_message || 'No messages yet'}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-xs text-gray-400">{chat.last_message_at || ''}</p>
                            {chat.assigned_agent_id && <p className="text-[10px] text-gray-500 mt-0.5">Agent #{chat.assigned_agent_id}</p>}
                          </div>
                          <span className="px-2.5 py-1 rounded-lg text-xs font-medium" style={{
                            background: statusColors[chat.status]?.bg || '#f3f4f6',
                            color: statusColors[chat.status]?.text || '#374151',
                          }}>
                            {(chat.status || 'open').replace('_', ' ')}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
              )}

              {/* Chat History */}
              {activeTab === 'history' && (
                chatHistory.length === 0 ?
                  renderEmpty('History', 'No Chat History', 'Past conversations will appear here once chats are resolved.') :
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="text-xs text-gray-500 border-b border-gray-50">
                          <th className="text-left px-4 py-3 font-medium">Contact</th>
                          <th className="text-center px-4 py-3 font-medium">Messages</th>
                          <th className="text-left px-4 py-3 font-medium">Agent</th>
                          <th className="text-center px-4 py-3 font-medium">Rating</th>
                          <th className="text-left px-4 py-3 font-medium">Status</th>
                          <th className="text-left px-4 py-3 font-medium">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {chatHistory.map((chat) => (
                          <tr key={chat.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">Contact #{chat.contact_id}</td>
                            <td className="px-4 py-3 text-center text-sm text-gray-700">{chat.total_messages || 0}</td>
                            <td className="px-4 py-3 text-xs text-gray-600">{chat.assigned_agent_id ? `Agent #${chat.assigned_agent_id}` : 'Bot'}</td>
                            <td className="px-4 py-3 text-center">
                              {chat.customer_rating ? (
                                <div className="flex items-center justify-center gap-0.5">
                                  {[...Array(5)].map((_, i) => (
                                    <Icons.Star key={i} className="w-3 h-3" style={{ color: i < chat.customer_rating ? '#F59E0B' : '#e5e7eb', fill: i < chat.customer_rating ? '#F59E0B' : 'none' }} />
                                  ))}
                                </div>
                              ) : <span className="text-xs text-gray-400">N/A</span>}
                            </td>
                            <td className="px-4 py-3">
                              <span className="px-2 py-0.5 rounded-md text-xs font-medium" style={{
                                background: statusColors[chat.status]?.bg || '#f3f4f6',
                                color: statusColors[chat.status]?.text || '#374151',
                              }}>{(chat.status || 'resolved').replace('_', ' ')}</span>
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-500">{chat.closed_at || chat.created_at || 'N/A'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
              )}

              {/* Departments */}
              {activeTab === 'departments' && (
                <div className="space-y-4">
                  <div className="flex justify-end">
                    <button className="flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-medium" style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}>
                      <Icons.Plus className="w-4 h-4" /> Add Department
                    </button>
                  </div>
                  {departments.length === 0 ?
                    renderEmpty('Building2', 'No Departments', 'Create departments to organize chat routing and agent assignment.') :
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {departments.map((dept) => (
                        <div key={dept.id} className="p-5 rounded-xl border border-gray-100 hover:border-green-200 hover:shadow-sm transition-all">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${dept.color || '#25D366'}14`, color: dept.color || '#25D366' }}>
                                <Icons.Building2 className="w-5 h-5" />
                              </div>
                              <div>
                                <h3 className="font-semibold text-gray-900">{dept.name}</h3>
                                <p className="text-xs text-gray-400">{dept.description || 'No description'}</p>
                              </div>
                            </div>
                            <button className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400">
                              <Icons.MoreVertical className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="p-2.5 rounded-lg bg-gray-50">
                              <p className="text-lg font-bold text-gray-900">{dept.max_concurrent_chats || 10}</p>
                              <p className="text-[10px] text-gray-500">Max Chats</p>
                            </div>
                            <div className="p-2.5 rounded-lg bg-gray-50">
                              <p className="text-lg font-bold text-gray-900">{dept.auto_assign ? 'Yes' : 'No'}</p>
                              <p className="text-[10px] text-gray-500">Auto Assign</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  }
                </div>
              )}

              {/* Agents */}
              {activeTab === 'agents' && (
                <div className="space-y-4">
                  <div className="flex justify-end">
                    <button className="flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-medium" style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}>
                      <Icons.UserPlus className="w-4 h-4" /> Add Agent
                    </button>
                  </div>
                  {agents.length === 0 ?
                    renderEmpty('Users', 'No Agents', 'Add agents to handle WhatsApp conversations with your customers.') :
                    <div className="space-y-3">
                      {agents.map((agent) => (
                        <div key={agent.id} className="flex items-center justify-between p-4 rounded-xl border border-gray-100 hover:border-green-200 transition-all">
                          <div className="flex items-center gap-4">
                            <div className="relative">
                              <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}>
                                {(agent.name || 'A').charAt(0)}
                              </div>
                              <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white" style={{ background: statusColors[agent.status]?.dot || '#9ca3af' }} />
                            </div>
                            <div>
                              <h3 className="font-semibold text-gray-900 text-sm">{agent.name}</h3>
                              <p className="text-xs text-gray-400">{agent.email}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-6">
                            <span className="text-xs text-gray-500 px-2 py-1 rounded-lg bg-gray-50 capitalize">{agent.role || 'agent'}</span>
                            <div className="text-center">
                              <p className="text-sm font-bold text-gray-900">{agent.total_resolved || 0}</p>
                              <p className="text-[10px] text-gray-400">Resolved</p>
                            </div>
                            {agent.avg_rating > 0 && (
                              <div className="flex items-center gap-1">
                                <Icons.Star className="w-3.5 h-3.5" style={{ color: '#F59E0B', fill: '#F59E0B' }} />
                                <span className="text-sm font-medium text-gray-700">{agent.avg_rating}</span>
                              </div>
                            )}
                            <span className="px-2 py-0.5 rounded-md text-xs font-medium" style={{
                              background: statusColors[agent.status]?.bg || '#f3f4f6',
                              color: statusColors[agent.status]?.text || '#374151',
                            }}>{agent.status || 'offline'}</span>
                            <button className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400">
                              <Icons.MoreVertical className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  }
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserChatSettings;
